import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { comprobanteService } from '../services/comprobanteService';
import type { ComprobanteFaltante, EntidadDinero } from '../types/comprobante';
import { cuentaParaRegularizacion } from '../utils/comprobantePlazo';
import { entidadPropia } from '../utils/comprobanteEntidad';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { claveEntidadDinero, vencidosDeLaEntidad, type VencidosCargados } from '../utils/ventanaRegularizacion';

interface RegularizacionValor {
  readonly entidad: EntidadDinero | null;
  /** Faltantes con más de 7 días de la entidad propia. Vacío = sin modo regularización. */
  readonly vencidos: readonly ComprobanteFaltante[];
  readonly recargar: () => void;
}

const RegularizacionContext = createContext<RegularizacionValor | undefined>(undefined);

export const RegularizacionProvider: React.FC<{ readonly children: React.ReactNode }> = ({ children }) => {
  const { usuario } = useAuth();
  const entidad = useMemo(() => entidadPropia(usuario), [usuario]);
  const [cargados, setCargados] = useState<VencidosCargados<ComprobanteFaltante> | null>(null);
  const entidadClave = claveEntidadDinero(entidad);
  // Sin entidad propia (admin_global), o con lo cargado para otra entidad (logout, cambio de
  // identidad, carga fallida), no hay modo regularización: no se hereda lo del actor anterior.
  const vencidos = useMemo(() => vencidosDeLaEntidad(cargados, entidadClave), [cargados, entidadClave]);

  const recargar = useCallback(() => {
    if (!entidad) return;
    const entidadClaveConsultada = claveEntidadDinero(entidad) ?? '';
    comprobanteService.getFaltantes(entidad)
      .then((faltantes) => {
        const ahora = new Date();
        setCargados({
          entidadClave: entidadClaveConsultada,
          faltantes: faltantes.filter((f) => cuentaParaRegularizacion(f.pendiente_desde, ahora)),
        });
      })
      .catch((e: unknown) => { reportarErrorInesperado(e, 'No pudimos revisar los comprobantes pendientes.'); });
  }, [entidad]);

  useEffect(() => { recargar(); }, [recargar]);

  const valor = useMemo(() => ({ entidad, vencidos, recargar }), [entidad, vencidos, recargar]);
  return <RegularizacionContext.Provider value={valor}>{children}</RegularizacionContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useRegularizacion(): RegularizacionValor {
  const contexto = useContext(RegularizacionContext);
  if (contexto === undefined) throw new Error('useRegularizacion debe usarse dentro de RegularizacionProvider');
  return contexto;
}
